import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Search, Truck } from '@/components/icons';
import { SectionHeader } from '@/components/ui/SectionHeader';
import { FilterBar, FilterField } from '@/components/ui/FilterBar';
import { Table } from '@/components/ui/Table';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { useToast } from '@/components/ui/useToast';
import { HandoffModal } from '@/components/ui/HandoffModal';
import { useAuth } from '@/auth/useAuth';
import { listInventoryCheck, type InvCheckRow } from '@/api/inventory-check';
import { prFromShortage } from '@/api/pr';
import { apiError } from '@/lib/apiError';

const STATUS_TONE: Record<InvCheckRow['stockStatus'], 'success' | 'warning' | 'danger'> = {
  'In Stock': 'success',
  'Low Stock': 'warning',
  Shortage: 'warning',
  'Out of Stock': 'danger',
};

export function InvCheckPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<InvCheckRow['stockStatus'] | ''>('');
  const [department, setDepartment] = useState('Warehouse');
  const [tab, setTab] = useState<'active' | 'history'>('active');
  const [prHandoffOpen, setPrHandoffOpen] = useState(false);
  const { hasPerm } = useAuth();

  const query = useQuery({
    queryKey: ['inventory-check', { dept: undefined }],
    queryFn: () => listInventoryCheck({}),
    refetchInterval: 30_000,
  });

  const rows = useMemo(() => {
    const all = (query.data ?? []).filter((r) => (tab === 'active' ? !r.hasPo : r.hasPo));
    return all.filter((r) => {
      if (statusFilter && r.stockStatus !== statusFilter) return false;
      return true;
    });
  }, [query.data, statusFilter, tab]);

  const shortageRows = useMemo(
    () => rows.filter((r) => r.shortage > 0 || r.stockStatus === 'Out of Stock'),
    [rows],
  );

  const prMutation = useMutation({
    mutationFn: () => {
      // Group shortage rows by MRN so each PR is linked to its source MRN
      const byMrn = new Map<number, typeof shortageRows>();
      for (const r of shortageRows) {
        const existing = byMrn.get(r.mrnId);
        if (existing) existing.push(r);
        else byMrn.set(r.mrnId, [r]);
      }
      return Promise.all(
        Array.from(byMrn.entries()).map(([mrnId, items]) =>
          prFromShortage({
            department,
            authority: 'HOD',
            mrnId,
            items: items.map((r) => ({
              itemId: r.itemId ?? undefined,
              itemName: r.itemName,
              qty: r.shortage > 0 ? r.shortage : r.reqQty,
              unit: r.unit,
              unitPrice: 0,
            })),
          }),
        ),
      );
    },
    onSuccess: (prs) => {
      qc.invalidateQueries({ queryKey: ['pr'] });
      qc.invalidateQueries({ queryKey: ['inventory-check'] });
      toast.push(`Created ${prs.length} PR(s) for ${shortageRows.length} shortage items`, 'success');
      navigate('/purchase/pr');
    },
    onError: (e: unknown) => {
      toast.push(apiError(e, 'PR generation failed'), 'error');
    },
  });

  return (
    <div>
      <SectionHeader
        title="Inventory Check"      />

      {/* Active / History tabs */}
      {(() => {
        const all = query.data ?? [];
        const activeCount   = all.filter((r) => !r.hasPo).length;
        const historyCount  = all.filter((r) => r.hasPo).length;
        return (
          <div className="mb-2 inline-flex gap-1 rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] p-1 sm:mb-2">
            {(['active', 'history'] as const).map((t) => {
              const active = tab === t;
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() => setTab(t)}
                  className={
                    'rounded-md px-3 py-1.5 text-[11px] font-bold capitalize transition sm:text-xs ' +
                    (active
                      ? 'bg-accent text-white shadow-sm'
                      : 'text-[var(--text-muted)] hover:bg-[var(--bg-soft)] hover:text-[var(--text-primary)]')
                  }
                >
                  {t === 'active' ? `Active (${activeCount})` : `History (${historyCount})`}
                </button>
              );
            })}
          </div>
        );
      })()}

      <FilterBar>
        <FilterField label="Search">
          <div className="relative w-full">
            <Search
              size={11}
              className="pointer-events-none absolute left-1.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)] sm:left-2.5"
              strokeWidth={2.25}
            />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              className="!pl-5 sm:!pl-8 sm:min-w-[240px]"
              aria-label="Search items or MRN number"
            />
          </div>
        </FilterField>
        <FilterField label="Department (for PR)">
          <Input
            value={department}
            onChange={(e) => setDepartment(e.target.value)}
            placeholder="Used when generating PR"
            className="sm:min-w-[160px]"
          />
        </FilterField>
        <FilterField label="Status">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as InvCheckRow['stockStatus'] | '')}
          >
            <option value="">All</option>
            <option>In Stock</option>
            <option>Low Stock</option>
            <option>Shortage</option>
            <option>Out of Stock</option>
          </Select>
        </FilterField>
        {/* Generate PR chip lives inside the filter block on both viewports.
            Mobile: full-width row below the fields (col-span-3) so the long
            label fits. Desktop: right-pinned via ml-auto. */}
        {tab === 'active' && shortageRows.length > 0 && (
          <button
            type="button"
            onClick={() => {
              if (!hasPerm('pr')) {
                setPrHandoffOpen(true);
                return;
              }
              prMutation.mutate();
            }}
            disabled={prMutation.isPending}
            className="col-span-3 inline-flex h-7 items-center justify-center gap-1 rounded-lg bg-accent px-2 text-[11px] font-bold text-white shadow-sm transition hover:brightness-110 disabled:opacity-60 sm:col-span-1 sm:ml-auto sm:h-8 sm:self-end sm:px-3 sm:text-xs"
          >
            {prMutation.isPending ? 'Generating…' : <><Truck size={14} className="mr-1 inline-block align-[-2px]" /> Generate PR for {shortageRows.length} Shortage Item{shortageRows.length === 1 ? '' : 's'}</>}
          </button>
        )}
      </FilterBar>

      <Table<InvCheckRow>
        storageKey="invcheck"
        title={`${tab === 'active' ? 'Active MRN Items' : 'History — PO Generated'} (${rows.length})`}
        count={`${shortageRows.length} require purchase`}
        rows={rows}
        search={search}
        rowKey={(r) => `${r.mrnId}:${r.mrnItemId}`}
        loading={query.isFetching && (query.data?.length ?? 0) === 0}
        columns={[
          { key: 'mrn', header: 'MRN #', align: 'center', cell: (r) => <span className="font-extrabold text-accent">{r.mrnNo}</span> },
          { key: 'item', header: 'Item', align: 'center', cell: (r) => <span className="font-bold">{r.itemName}</span> },
          { key: 'req', header: 'Req Qty', align: 'center', cell: (r) => r.reqQty },
          {
            key: 'current',
            header: 'Current',
            align: 'center',
            cell: (r) => (
              <span className={r.currentStock <= 0 ? 'font-extrabold text-danger' : 'font-bold'}>
                {r.currentStock}
              </span>
            ),
          },
          { key: 'available', header: 'Available', align: 'center', cell: (r) => r.available },
          {
            key: 'shortage',
            header: 'Shortage',
            align: 'center',
            cell: (r) => (
              <span className={r.shortage > 0 ? 'font-extrabold text-danger' : 'text-[var(--text-muted)]'}>
                {r.shortage}
              </span>
            ),
          },
          { key: 'unit', header: 'Unit', align: 'center', cell: (r) => r.unit },
          { key: 'reorder', header: 'Reorder', align: 'center', cell: (r) => r.reorder ?? '—' },
          {
            key: 'status',
            header: 'Status',
            align: 'center',
            cell: (r) => <Badge tone={STATUS_TONE[r.stockStatus]}>{r.stockStatus}</Badge>,
          },
        ]}
      />

      {prHandoffOpen && (
        <HandoffModal
          open
          onClose={() => setPrHandoffOpen(false)}
          action="generate a Purchase Requisition for these shortage items"
          payload={{
            moduleId: 'pr',
            subject: `PR needed — ${shortageRows.length} shortage item${shortageRows.length === 1 ? '' : 's'}`,
            body: `Please raise a Purchase Requisition for the current shortage items in inventory check. Top items: ${shortageRows.slice(0, 3).map((r) => r.itemName).join(', ')}${shortageRows.length > 3 ? `, +${shortageRows.length - 3} more` : ''}.`,
            entityType: 'invcheck',
            actionHref: '/purchase/invcheck',
          }}
        />
      )}
    </div>
  );
}
